import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as RunIcon,
  Science as DryRunIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import HelpIcon from '../../components/help/HelpIcon';
import { toServerDate } from '../../timezone';

const fmtNum = (n) => {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || Number.isNaN(v) || v < 0) return '—';
  return v.toLocaleString();
};

const fmtTime = (iso) => (toServerDate(iso)?.toLocaleString() ?? '—');

const statusColor = (s) => ({
  SUCCESS: 'success',
  FAILED: 'error',
  PENDING_FIRST_RUN: 'default',
}[s] || 'default');

const DEFAULT_DAYS = 30;

const EMPTY_FORM = {
  id: null,
  organizationId: '',
  organizationName: '',
  tableName: 'logs',
  retentionDays: DEFAULT_DAYS,
  enabled: true,
};

/**
 * Per-tenant retention policy manager. Lives in the Tenant Routing tab
 * under the data-usage panel. Each policy corresponds to one
 * (organization, table) pair — the scheduler runs an ALTER DELETE once a
 * day to prune rows older than retentionDays.
 */
const ClickHouseRetentionPanel = ({ organizations }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tables, setTables] = useState([]);

  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);

  const orgById = useMemo(() => {
    const m = new Map();
    (organizations || []).forEach(o => m.set(Number(o.id ?? o.organizationId), o));
    return m;
  }, [organizations]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pol, tbl] = await Promise.all([
        billingService.retentionListAll(),
        billingService.retentionTables(),
      ]);
      setPolicies(Array.isArray(pol.data) ? pol.data : []);
      setTables(tbl.data?.tables ? Array.from(tbl.data.tables) : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Load failed');
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({
      ...EMPTY_FORM,
      organizationId: organizations && organizations.length > 0
        ? (organizations[0].id ?? organizations[0].organizationId) : '',
      organizationName: organizations && organizations.length > 0
        ? (organizations[0].name ?? organizations[0].organizationName ?? '') : '',
    });
    setDryRunResult(null);
    setDialog({ open: true, editing: null });
  };

  const openEdit = (p) => {
    setForm({
      id: p.id,
      organizationId: p.organizationId,
      organizationName: p.organizationName,
      tableName: p.tableName,
      retentionDays: p.retentionDays,
      enabled: p.enabled,
    });
    setDryRunResult(null);
    setDialog({ open: true, editing: p });
  };

  const closeDialog = () => {
    setDialog({ open: false, editing: null });
    setForm(EMPTY_FORM);
    setDryRunResult(null);
  };

  const dryRun = async () => {
    if (!form.organizationId || !form.tableName || !Number(form.retentionDays)) return;
    setDryRunning(true);
    setDryRunResult(null);
    try {
      const res = await billingService.retentionDryRun({
        organizationId: Number(form.organizationId),
        tableName: form.tableName,
        retentionDays: Number(form.retentionDays),
      });
      setDryRunResult(res.data);
    } catch (e) {
      setDryRunResult({ error: e?.response?.data?.error || e.message });
    } finally {
      setDryRunning(false);
    }
  };

  const save = async () => {
    if (!form.organizationId) return;
    setSaving(true);
    try {
      const org = orgById.get(Number(form.organizationId));
      await billingService.retentionUpsert({
        organizationId: Number(form.organizationId),
        organizationName: form.organizationName || org?.name || org?.organizationName || null,
        tableName: form.tableName,
        retentionDays: Number(form.retentionDays),
        enabled: form.enabled,
      });
      closeDialog();
      load();
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete retention policy for ${p.tableName} on ${p.organizationName || ('org ' + p.organizationId)}?`)) return;
    try {
      await billingService.retentionDelete(p.id);
      load();
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Delete failed');
    }
  };

  const runNow = async (p) => {
    if (!window.confirm(`Run retention for ${p.tableName} on ${p.organizationName || ('org ' + p.organizationId)} now? ` +
        `This will kick off an ALTER DELETE on rows older than ${p.retentionDays} days. Cannot be undone.`)) return;
    try {
      await billingService.retentionRunNow(p.id);
      load();
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Run failed');
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">Per-tenant retention policies</Typography>
        <HelpIcon docKey="tenant_retention" />
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New policy
        </Button>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && policies.length === 0 && (
        <Alert severity="info">
          No per-tenant retention policies yet. The schema-level TTL (365 days) still applies to every tenant.
          Create a policy to prune a specific (tenant, table) to a shorter window — e.g. Free-plan tenants at 30 days on the <code>logs</code> table.
        </Alert>
      )}

      {policies.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Table</TableCell>
              <TableCell align="right">Retention</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Last run</TableCell>
              <TableCell align="right">Last rows</TableCell>
              <TableCell>Next run</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {policies.map(p => (
              <TableRow key={p.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{p.organizationName || ('org ' + p.organizationId)}</Typography>
                </TableCell>
                <TableCell><Chip size="small" label={p.tableName} /></TableCell>
                <TableCell align="right">{p.retentionDays} days</TableCell>
                <TableCell>
                  <Chip size="small"
                        color={p.enabled ? 'success' : 'default'}
                        label={p.enabled ? 'enabled' : 'disabled'} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" color={statusColor(p.lastRunStatus)}
                          label={p.lastRunStatus || 'PENDING_FIRST_RUN'} />
                    <Tooltip title={fmtTime(p.lastRunAt)}>
                      <Typography variant="caption">
                        {p.lastRunAt ? fmtTime(p.lastRunAt) : '—'}
                      </Typography>
                    </Tooltip>
                    {p.lastError && (
                      <Tooltip title={p.lastError}>
                        <Chip size="small" color="error" label="err" sx={{ fontSize: 10 }} />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{fmtNum(p.lastRunRows)}</TableCell>
                <TableCell>
                  <Typography variant="caption">{fmtTime(p.nextRunAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="Run retention now">
                    <span>
                      <Button size="small" startIcon={<RunIcon />} onClick={() => runNow(p)}>Run</Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit policy">
                    <span>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(p)}>Edit</Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete policy">
                    <span>
                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(p)}>
                        Delete
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {dialog.editing ? 'Edit retention policy' : 'New retention policy'}
            <HelpIcon docKey="tenant_retention" />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Select
              fullWidth
              size="small"
              value={form.organizationId}
              onChange={(e) => {
                const v = e.target.value;
                const org = orgById.get(Number(v));
                setForm(f => ({
                  ...f,
                  organizationId: v,
                  organizationName: org?.name || org?.organizationName || f.organizationName,
                }));
                setDryRunResult(null);
              }}
              disabled={!!dialog.editing}
              displayEmpty>
              <MenuItem value=""><em>Select tenant…</em></MenuItem>
              {(organizations || []).map(o => {
                const id = o.id ?? o.organizationId;
                const name = o.name || o.organizationName || '';
                const slug = o.slug || o.organizationSlug || '';
                return (
                  <MenuItem key={id} value={id}>
                    {name} {slug && <Chip size="small" sx={{ ml: 1 }} label={slug} variant="outlined" />}
                  </MenuItem>
                );
              })}
            </Select>

            <Select
              fullWidth
              size="small"
              value={form.tableName}
              onChange={(e) => { setForm(f => ({ ...f, tableName: e.target.value })); setDryRunResult(null); }}
              disabled={!!dialog.editing}>
              {tables.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Retention (days)"
              value={form.retentionDays}
              onChange={(e) => { setForm(f => ({ ...f, retentionDays: e.target.value })); setDryRunResult(null); }}
              helperText="Must be > 0 and ≤ 3650. Lower values = more aggressive pruning."
              inputProps={{ min: 1, max: 3650 }}
            />

            <FormControlLabel
              control={<Switch checked={form.enabled}
                               onChange={(e) => setForm(f => ({ ...f, enabled: e.target.checked }))} />}
              label={form.enabled ? 'Enabled — scheduler runs this policy daily' : 'Disabled — scheduler skips'}
            />

            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Dry run</Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<DryRunIcon />} onClick={dryRun}
                        disabled={dryRunning || !form.organizationId || !form.retentionDays}>
                  {dryRunning ? 'Checking…' : 'How many rows would this delete?'}
                </Button>
              </Stack>
              {dryRunning && <LinearProgress />}
              {dryRunResult?.error && <Alert severity="error">{dryRunResult.error}</Alert>}
              {dryRunResult && !dryRunResult.error && (
                <>
                  <Alert severity={dryRunResult.rowsWouldDelete > 0 ? 'warning' : 'info'}>
                    Would delete <strong>{fmtNum(dryRunResult.rowsWouldDelete)}</strong> rows right now.
                  </Alert>
                  <Typography variant="caption"
                              sx={{ display: 'block', mt: 1,
                                    fontFamily: 'ui-monospace, Menlo, monospace',
                                    wordBreak: 'break-all' }}>
                    {dryRunResult.sql}
                  </Typography>
                </>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save}
                  disabled={saving || !form.organizationId || !form.tableName || !form.retentionDays}>
            {saving ? 'Saving…' : dialog.editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ClickHouseRetentionPanel;
